import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import {
    ContractorDocument,
    DOCUMENT_TYPE_LABELS,
} from './types';

export type ReviewMode = 'approve' | 'reject' | 'request-reupload';

interface Props {
    companyScoped?: boolean;
    open: boolean;
    mode: ReviewMode;
    document: ContractorDocument | null;
    submitting: boolean;
    error: string | null;
    onClose: () => void;
    onSubmit: (
        action: {
            status: 'APPROVED' | 'REJECTED';
            rejectionReason?: string;
            expiresAt?: string | null;
        }
    ) => void;
}

const REUPLOAD_DEFAULT_REASON =
    'This document could not be approved. Please upload a clear, current copy with all required fields completed and visible.';
const MAX_REASON_LEN = 500;

/**
 * Modal that confirms an admin's review action on a contractor document
 * (#305). One dialog handles three flows:
 *   - Approve: no body, single confirm button.
 *   - Reject / request re-upload: the approved worker-facing default message
 *     plus an optional Admin explanation that is also visible to the worker.
 *
 * The dialog is purely presentational. The parent owns the mutation, the
 * loading state, and the error string — this matches the pattern used by
 * the bilateral-review dialog (`ContractorRatingDialog`).
 */
export default function DocumentReviewDialog({
    companyScoped = false,
    open,
    mode,
    document,
    submitting,
    error,
    onClose,
    onSubmit,
}: Props) {
    const [adminExplanation, setAdminExplanation] = useState('');
    const [expiry, setExpiry] = useState('');
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        if (!open) return;
        // The worker always receives the approved default message. Reset the
        // optional worker-visible explanation so text cannot leak between
        // reviews.
        setAdminExplanation('');
        setTouched(false);
        setExpiry(document?.expiresAt?.slice(0, 10) ?? '');
    }, [open, mode, document?.id]);

    const trimmedExplanation = adminExplanation.trim();
    const rejectionReason = trimmedExplanation
        ? `${REUPLOAD_DEFAULT_REASON}\n\nAdmin note: ${trimmedExplanation}`
        : REUPLOAD_DEFAULT_REASON;
    const reasonInvalid = rejectionReason.length > MAX_REASON_LEN;

    const title =
        mode === 'approve'
            ? 'Approve document'
            : mode === 'reject'
              ? 'Reject document'
              : 'Request re-upload';

    const submitLabel =
        mode === 'approve'
            ? 'Approve & notify'
            : mode === 'reject'
              ? 'Reject & notify'
              : 'Send request';

    const handleSubmit = () => {
        if (mode === 'approve') {
            onSubmit({
                status: 'APPROVED',
                expiresAt: expiry
                    ? new Date(`${expiry}T23:59:59.999Z`).toISOString()
                    : null,
            });
            return;
        }
        if (reasonInvalid) {
            setTouched(true);
            return;
        }
        onSubmit({ status: 'REJECTED', rejectionReason });
    };

    const docLabel = document
        ? `${DOCUMENT_TYPE_LABELS[document.type]} — ${
              document.originalFilename || document.filename
          }`
        : '';

    return (
        <Dialog className={companyScoped ? `company-worker-document-review review-${mode}` : undefined} open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle data-testid="document-review-dialog-title">{title}</DialogTitle>
            <DialogContent>
                {error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                ) : null}
                <Stack spacing={2} mt={1}>
                    {document ? (
                        <Typography variant="body2" color="text.secondary">
                            {docLabel}
                        </Typography>
                    ) : null}
                    {mode === 'approve' ? (
                        <Stack spacing={2}>
                            <Typography variant="body2">
                                {companyScoped
                                    ? 'Approve this document for this company. The worker will be notified and the company acceptance status will update on their profile.'
                                    : 'Mark this document as approved. The contractor will be notified and the status badge will update on their profile.'}
                            </Typography>
                            <TextField
                                label="Expiration date (optional)"
                                type="date"
                                value={expiry}
                                onChange={(event) => setExpiry(event.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                inputProps={{
                                    min: new Date().toISOString().slice(0, 10),
                                    'data-testid': 'document-review-expiry',
                                }}
                                helperText={expiry
                                    ? 'Use for time-bound documents. Clear the date when the document does not expire.'
                                    : 'No expiration. Approval will save this document without an expiration date.'}
                            />
                            {expiry ? (
                                <Button onClick={() => setExpiry('')} sx={{ alignSelf: 'flex-start' }}>
                                    Use no expiration
                                </Button>
                            ) : null}
                        </Stack>
                    ) : (
                        <Stack spacing={2}>
                            <Alert severity="info" data-testid="document-review-default-message">
                                <Typography variant="caption" fontWeight={700} display="block">
                                    Default message shown to the worker
                                </Typography>
                                {REUPLOAD_DEFAULT_REASON}
                            </Alert>
                            <TextField
                                label="Admin explanation (optional · shown to worker)"
                                value={adminExplanation}
                                onChange={(e) => setAdminExplanation(e.target.value)}
                                onBlur={() => setTouched(true)}
                                multiline
                                minRows={3}
                                fullWidth
                                inputProps={{
                                    maxLength: MAX_REASON_LEN,
                                    'data-testid': 'document-review-explanation',
                                }}
                                error={touched && reasonInvalid}
                                helperText={
                                    touched && reasonInvalid
                                        ? `Default message plus explanation must be at most ${MAX_REASON_LEN} characters.`
                                        : 'Optional context is appended to the default message and is visible to the worker.'
                                }
                            />
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting} color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={submitting || reasonInvalid}
                    variant="contained"
                    color={mode === 'approve' ? 'success' : 'error'}
                    data-testid="document-review-submit"
                >
                    {submitting ? 'Saving…' : submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
